import { For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import {
  ArrowLeft,
  Download,
  Forward,
  Lock,
  MailOpen,
  Paperclip,
  Reply,
  ReplyAll,
  Star,
  X,
} from "@fleet/icons";

import { cn } from "@cartero/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import type { MailFolder, MessageDetail } from "../api";
import { bareAddress, displayFromAddress, formatMailDate, formatSize } from "./format";
import { ReaderSkeleton } from "./skeletons";

interface MessageReaderProps {
  detail: MessageDetail | undefined;
  loading: boolean;
  folders: MailFolder[];
  activeFolder: string;
  onReply: () => void;
  onReplyAll: () => void;
  onForward: () => void;
  onToggleFlag: () => void;
  onToggleSeen: () => void;
  onMove: (dest: string) => void;
  onExportEml: () => void;
  onClose: () => void;
}

function ActionBtn(props: {
  label: string;
  icon: typeof Reply;
  onClick: () => void;
  active?: boolean;
  testid?: string;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      class={cn("gap-1.5", props.active && "text-amber-400")}
      onClick={() => props.onClick()}
      aria-label={props.label}
      data-testid={props.testid}
    >
      <props.icon class={cn("h-4 w-4", props.active && "fill-amber-400")} />
      <span class="hidden sm:inline">{props.label}</span>
    </Button>
  );
}

export function MessageReader(props: MessageReaderProps) {
  const { t, locale } = useI18n();

  const moveOptions = () =>
    props.folders
      .filter((f) => f.name !== props.activeFolder)
      .map((f) => ({ value: f.name, label: f.name }));

  return (
    <section
      class="flex min-h-0 flex-1 flex-col border-l border-border/40 bg-card/5"
      data-testid="mail-reader"
    >
      <Show when={!props.loading} fallback={<ReaderSkeleton />}>
        <Show
          when={props.detail}
          fallback={
            <div class="flex flex-1 items-center justify-center p-8 text-center text-sm text-muted-foreground">
              {t(msg`Select a message to read it here.`)}
            </div>
          }
        >
          {(detail) => (
            <>
              <header class="flex flex-wrap items-center gap-1 border-b border-border/40 px-4 py-2">
                <button
                  type="button"
                  class="mr-1 flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent/10 lg:hidden"
                  onClick={() => props.onClose()}
                  aria-label={t(msg`Back`)}
                >
                  <ArrowLeft class="h-4 w-4" />
                </button>
                <ActionBtn
                  label={t(msg`Reply`)}
                  icon={Reply}
                  onClick={props.onReply}
                  testid="mail-reply"
                />
                <ActionBtn
                  label={t(msg`Reply all`)}
                  icon={ReplyAll}
                  onClick={props.onReplyAll}
                  testid="mail-reply-all"
                />
                <ActionBtn
                  label={t(msg`Forward`)}
                  icon={Forward}
                  onClick={props.onForward}
                  testid="mail-forward"
                />
                <ActionBtn
                  label={detail().flagged ? t(msg`Unstar`) : t(msg`Star`)}
                  icon={Star}
                  active={detail().flagged}
                  onClick={props.onToggleFlag}
                  testid="mail-reader-flag"
                />
                <ActionBtn
                  label={detail().seen ? t(msg`Mark unread`) : t(msg`Mark read`)}
                  icon={MailOpen}
                  onClick={props.onToggleSeen}
                  testid="mail-toggle-seen"
                />
                <ActionBtn
                  label={t(msg`Export .eml`)}
                  icon={Download}
                  onClick={props.onExportEml}
                  testid="mail-export-eml"
                />
                <div class="ml-auto flex items-center gap-1">
                  <Show when={moveOptions().length > 0}>
                    <div class="w-36">
                      <Select<string>
                        aria-label={t(msg`Move to folder`)}
                        placeholder={t(msg`Move to…`)}
                        value={undefined}
                        onChange={(dest) => props.onMove(dest)}
                        options={moveOptions()}
                        triggerClass="h-8"
                      />
                    </div>
                  </Show>
                  <button
                    type="button"
                    class="hidden h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent/10 lg:flex"
                    onClick={() => props.onClose()}
                    aria-label={t(msg`Close`)}
                  >
                    <X class="h-4 w-4" />
                  </button>
                </div>
              </header>

              <div class="mx-auto min-h-0 w-full max-w-[72ch] flex-1 overflow-y-auto px-6 py-6">
                <h1
                  class="font-serif text-2xl font-semibold leading-snug text-foreground"
                  data-testid="mail-reader-subject"
                >
                  {detail().subject || t(msg`(no subject)`)}
                  <Show when={detail().isEncrypted}>
                    <span
                      class="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 align-middle text-[11px] font-medium text-emerald-600 ring-1 ring-emerald-500/30 dark:text-emerald-400"
                      data-testid="mail-encrypted-badge"
                    >
                      <Lock class="h-3 w-3" /> {t(msg`Sealed`)}
                    </span>
                  </Show>
                </h1>
                <div class="mt-1 flex flex-wrap items-baseline gap-x-2 text-sm">
                  <span class="font-medium text-foreground">
                    {displayFromAddress(detail().from)}
                  </span>
                  <span class="text-xs text-muted-foreground/70">{bareAddress(detail().from)}</span>
                  <span class="ml-auto text-xs text-muted-foreground/70">
                    {formatMailDate(detail().date, locale())}
                  </span>
                </div>
                <p class="mt-0.5 text-xs text-muted-foreground/80">
                  {t(msg`To:`)} {detail().to.join(", ")}
                </p>

                <Show when={detail().attachments.length > 0}>
                  <ul class="mt-3 flex flex-wrap gap-2">
                    <For each={detail().attachments}>
                      {(att) => (
                        <li class="flex items-center gap-1.5 rounded-md border border-border/50 bg-card/40 px-2.5 py-1.5 text-xs">
                          <Paperclip class="h-3.5 w-3.5 text-muted-foreground/70" />
                          <span class="max-w-40 truncate">{att.filename}</span>
                          <span class="text-muted-foreground/60">{formatSize(att.size)}</span>
                        </li>
                      )}
                    </For>
                  </ul>
                </Show>

                <div class="mt-5 border-t border-border/30 pt-6" data-testid="mail-reader-body">
                  <Show
                    when={detail().textBody}
                    fallback={
                      <Show
                        when={detail().htmlBody}
                        fallback={
                          <p class="text-sm text-muted-foreground">{t(msg`(empty message)`)}</p>
                        }
                      >
                        {/* Untrusted HTML in a scriptless sandboxed iframe (no
                            same-origin, no scripts) so remote email markup can't
                            run JS or read the app's cookies. */}
                        <iframe
                          title={t(msg`Message content`)}
                          sandbox=""
                          srcdoc={detail().htmlBody ?? ""}
                          class="h-[60vh] w-full rounded-lg border border-border/30 bg-white"
                        />
                      </Show>
                    }
                  >
                    {/* Long-form reading: editorial serif at a comfortable
                        measure and leading — the "letter" you actually read. */}
                    <pre class="whitespace-pre-wrap break-words font-serif text-[15px] leading-relaxed text-foreground/90">
                      {detail().textBody}
                    </pre>
                  </Show>
                </div>
              </div>
            </>
          )}
        </Show>
      </Show>
    </section>
  );
}
